import api from '../lib/api';
import type { CampaignLead, CampaignLeadFormData, CampaignLeadFilters, CampaignLeadOptions } from '../types/campaignLead';
import type { PaginatedResponse } from '../types/api';

class CampaignLeadService {
  async getAll(
    page: number = 1,
    limit: number = 20,
    filters?: CampaignLeadFilters
  ): Promise<PaginatedResponse<CampaignLead>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.search) params.append('search', filters.search);
    if (filters?.campaign_id) params.append('campaign_id', filters.campaign_id.toString());
    if (filters?.lead_id) params.append('lead_id', filters.lead_id.toString());
    if (filters?.ticket_id) params.append('ticket_id', filters.ticket_id.toString());
    if (filters?.stage) params.append('stage', filters.stage);
    if (filters?.score_min !== undefined) params.append('score_min', filters.score_min.toString());
    if (filters?.score_max !== undefined) params.append('score_max', filters.score_max.toString());
    if (filters?.action) params.append('action', filters.action);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.archived) params.append('archived', filters.archived);

    const response = await api.get(`/mkt-campaign-leads?${params.toString()}`);
    return response.data;
  }

  async getById(id: number): Promise<CampaignLead> {
    const response = await api.get(`/mkt-campaign-leads/${id}`);
    return response.data;
  }

  async create(data: CampaignLeadFormData): Promise<CampaignLead> {
    const response = await api.post('/mkt-campaign-leads', data);
    return response.data;
  }

  async update(id: number, data: Partial<CampaignLeadFormData>): Promise<CampaignLead> {
    const response = await api.put(`/mkt-campaign-leads/${id}`, data);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/mkt-campaign-leads/${id}`);
  }

  async archive(id: number): Promise<CampaignLead> {
    const response = await api.patch(`/mkt-campaign-leads/${id}/archive`);
    return response.data;
  }

  async setRepresentative(id: number, representative_id: number): Promise<CampaignLead> {
    const response = await api.patch(`/mkt-campaign-leads/${id}/representative`, {
      representative_id,
    });
    return response.data;
  }

  async getOptions(): Promise<CampaignLeadOptions> {
    const response = await api.get('/mkt-campaign-leads/options');
    return response.data;
  }
}

export const campaignLeadService = new CampaignLeadService();
